<template>
  <div
    v-if="products.length > 0"
    class="complete-the-look"
    data-testid="complete-the-set"
  >
    <div class="container">
      <div class="grid">
        <div class="products">
          <ProductCardHelper
            v-for="product in orderedProducts"
            :key="product.id"
            v-slot="{ primaryMedia, secondaryMedia }"
            :product="product"
            image-type="flatlayImages"
          >
            <ProductCard
              :product="product"
              :media="primaryMedia"
              :hover-media="secondaryMedia"
              remove-add-to-bag
            />
          </ProductCardHelper>
        </div>
        <div class="summary">
          <h2 class="title">Complete the Set</h2>
          <p class="text">Summary</p>
          <form class="form" @submit.prevent="handleFormSubmit">
            <div class="product-list">
              <SetProduct
                v-for="(product, index) in orderedProducts"
                :key="product.id"
                :product="product"
                :class="`product-${index}`"
                @variantSelected="
                  (variant) => handleVariantSelected(variant, product.id)
                "
              />
            </div>
            <div class="actions">
              <p class="total">
                Total Set Price: <b>{{ formattedTotalPrice }}</b>
              </p>

              <Button
                :loading="loading"
                type="submit"
                width="full"
                theme="outline"
              >
                Add All To Bag
              </Button>

              <p class="errors">{{ errorMessages[0] }}</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import productIdQuery from "@/graphql/queries/ProductId.gql";
import useVuelidate from "@vuelidate/core";
import { mapActions, mapState } from "vuex";
import cloneDeep from "lodash-es/cloneDeep";
import Button from "@/components/ButtonV3.vue";
import SetProduct from "@/components/product-display/complete-the-set/SetProductV2.vue";
import ProductCard from "@/components/ProductCard.vue";
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";

export default {
  name: "CompleteTheSet",
  components: {
    SetProduct,
    Button,
    ProductCard,
    ProductCardHelper,
  },
  setup() {
    const v = useVuelidate();

    return { v };
  },
  data() {
    return {
      products: [],
      selectedVariants: {},
      loading: false,
      isAddedSetToBagOpen: false,
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    productId() {
      return this.product?.id.split("/Product/")[1];
    },
    orderedProducts() {
      const products = cloneDeep(this.products);
      return products.sort((a, b) => a.order - b.order);
    },
    totalPrice() {
      const total = this.products.reduce((acc, product) => {
        return acc + parseFloat(product.priceRange.minVariantPrice.amount);
      }, 0);

      return {
        amount: total,
        currencyCode: this.products[0].priceRange.minVariantPrice.currencyCode,
      };
    },
    formattedTotalPrice() {
      return this.$filters.toCurrency(
        this.totalPrice.amount,
        this.totalPrice.currencyCode,
        0
      );
    },
    errorMessages() {
      return this.v.$errors.map((error) => error.$message);
    },
  },
  async created() {
    const groupedProductIds = await this.fetchGroupedProductIds();

    if (!groupedProductIds.length) {
      return false;
    }

    await this.getProducts(groupedProductIds);
  },
  methods: {
    ...mapActions("cart", ["addCartItem", "setCartOpen"]),
    async fetchGroupedProductIds() {
      if (!this.productId) {
        return;
      }

      const url = `${process.env.PRODUCT_GROUPING_GADGET_URL}/grouped-products/${this.productId}`;

      const resp = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) {
        throw new Error("Error fetching grouped products");
      }

      const data = await resp.json();

      return data?.products || [];
    },
    async getProducts(products) {
      const promises = products.map(({ id }) => {
        return this.$apollo.query({
          query: productIdQuery,
          variables: {
            productId: "gid://shopify/Product/" + id,
          },
        });
      });

      const values = await Promise.all(promises);
      values.forEach((value) => {
        const product = value.data.product;

        const order = products.find(
          (p) => "gid://shopify/Product/" + p.id === product.id
        ).order;

        this.products.push({ ...product, order });
      });
    },
    handleVariantSelected(variantId, productId) {
      this.selectedVariants[productId] = variantId;
    },
    async handleFormSubmit() {
      this.v.$touch();

      if (this.v.$invalid) {
        return;
      }

      this.loading = true;

      for (const [key, value] of Object.entries(this.selectedVariants)) {
        await this.addCartItem({ productId: key, variantId: value });
      }

      this.setCartOpen(true);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.complete-the-look {
  padding: 20px 0;

  @include respond-above(md) {
    padding: 80px 0 120px;
  }
}

.summary {
  @include respond-above(md) {
    padding: 0 80px;
  }

  .text {
    display: none;
    @include utility-label(14px);
    padding: 24px 0;

    @include respond-above(md) {
      display: block;
    }
  }

  .product-list {
    border-bottom: 1px solid $color-grey-light;
  }

  .actions {
    padding-top: 40px;
    text-align: center;
  }

  .total {
    @include utility-label(16px);
    text-transform: uppercase;
    margin-bottom: 24px;

    @include respond-above(md) {
      text-align: left;
    }
  }
}

.products {
  display: none;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 100px;

  @include respond-above(md) {
    display: grid;
    padding: 0 80px;
  }
}

.grid {
  @include respond-above(md) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}

.title {
  @include editorial-headline(32px);

  @include respond-above(md) {
    @include editorial-headline(40px);
  }
}

.errors {
  color: $color-red;
  margin-top: 20px;
  @include utility-label(14px);
}
</style>
